/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { ITextProps, ITextSlots } from '@fluentui/react/lib/Text';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';
import { numberToPercentageString } from 'src/core/utils/number';
import { commonDemosMessages } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonDemoMessages';
import { resultCardStyles, resultCardTitleStyles } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonStyles';
import { ConfidenceScores, SentimentResultDocument } from 'src/languageStudio/core/services/textAnalytics/textAnalyticsSwaggers';

export type SentimentCardProps = {
    confidenceDetails: ConfidenceScores;
    sentimentType: SentimentResultDocument['sentiment'];

    /**
     * Indicates whether the card displays the sentiment
     * of the whole document or just a sentence.
     */
    isDocument: boolean;
};

const messages = defineMessages({
    docSentiment: {
        id: 'languageStudio.sentimentCard.docSentiment',
        defaultMessage: 'Document sentiment'
    },
    sentenceSentiment: {
        id: 'languageStudio.sentimentCard.sentenceSentiment',
        defaultMessage: 'Sentence sentiment'
    },
    positive: {
        id: 'languageStudio.sentimentCard.positive',
        defaultMessage: 'Positive'
    },
    negative: {
        id: 'languageStudio.sentimentCard.negative',
        defaultMessage: 'Negative'
    },
    neutral: {
        id: 'languageStudio.sentimentCard.neutral',
        defaultMessage: 'Neutral'
    }
});

const theme = getScopedTheme('SentimentDemoPage');
const { styleDiv } = getStylist('SentimentDemoPage');

const capitalTextStyles = classNamesFunction<ITextProps, IComponentStyles<ITextSlots>>()({ root: { textTransform: 'uppercase' } });
const sentimentCardStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { width: '223px', minHeight: '150px' }
});

const LegendItem = styleDiv<{ color: string }>(
    'PositiveLegendItem',
    { width: '8px', height: '8px', marginRight: '8px', display: 'block' },
    ({ customProps }) => ({ backgroundColor: customProps.color })
);

export const SentimentCard = (props: SentimentCardProps) => {
    const { isDocument, sentimentType, confidenceDetails } = props;
    const { localize } = useLocalization();

    return (
        <Stack tokens={{ childrenGap: 7 }} styles={mergeStyleSets(resultCardStyles, sentimentCardStyles)}>
            <LuisText fontWeight={500} styles={resultCardTitleStyles(theme.labelColor)}>
                {localize(isDocument ? messages.docSentiment : messages.sentenceSentiment)}
            </LuisText>

            <Stack>
                <LuisText styles={{ root: { textTransform: 'capitalize' } }}>{sentimentType}</LuisText>
                <LuisText variant="smallPlus">
                    {localize(commonDemosMessages.confidenceLabel)} {numberToPercentageString({ ratio: confidenceDetails.positive })}
                </LuisText>
            </Stack>

            <Stack horizontal tokens={{ childrenGap: 8, padding: '10px 0 0' }}>
                <Stack horizontalAlign="center" tokens={{ childrenGap: 5 }}>
                    <LuisText>
                        <LegendItem customProps={{ color: theme.positiveSentimentColor }} />
                        {numberToPercentageString({ ratio: confidenceDetails.positive })}
                    </LuisText>
                    <LuisText variant="smallPlus" styles={capitalTextStyles}>
                        {localize(messages.positive)}
                    </LuisText>
                </Stack>

                <Stack horizontalAlign="center" tokens={{ childrenGap: 5 }}>
                    <LuisText>
                        <LegendItem customProps={{ color: theme.neutralSentimentColor }} />
                        {numberToPercentageString({ ratio: confidenceDetails.neutral })}
                    </LuisText>

                    <LuisText variant="smallPlus" styles={capitalTextStyles}>
                        {localize(messages.neutral)}
                    </LuisText>
                </Stack>

                <Stack horizontalAlign="center" tokens={{ childrenGap: 5 }}>
                    <LuisText>
                        <LegendItem customProps={{ color: theme.negativeSentimentColor }} />
                        {numberToPercentageString({ ratio: confidenceDetails.negative })}
                    </LuisText>

                    <LuisText variant="smallPlus" styles={capitalTextStyles}>
                        {localize(messages.negative)}
                    </LuisText>
                </Stack>
            </Stack>
        </Stack>
    );
};
